/**
 * Banner Dismissal Hook
 *
 * Manages the dismissed state of page info banners using localStorage.
 * Each banner's state is tracked independently by pageId.
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'yallyhub_banner_dismissed_';

export interface UseBannerDismissalReturn {
  /** Whether the banner has been dismissed by the user */
  isDismissed: boolean;
  /** Dismiss the banner and persist the state */
  dismiss: () => void;
  /** Reset the dismissal state (useful for debugging or re-showing banners) */
  reset: () => void;
}

/**
 * Hook to manage banner dismissal state
 *
 * @param pageId - Unique identifier for the page/banner
 * @returns Object with isDismissed state and dismiss/reset functions
 *
 * @example
 * ```tsx
 * const { isDismissed, dismiss } = useBannerDismissal('dd-settings');
 *
 * if (isDismissed) return null;
 *
 * return <Banner onClose={dismiss} />;
 * ```
 */
export function useBannerDismissal(pageId: string): UseBannerDismissalReturn {
  const storageKey = `${STORAGE_KEY_PREFIX}${pageId}`;

  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch (error) {
      console.error('Error reading banner dismissal state:', error);
      return false;
    }
  });

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(storageKey, 'true');
      setIsDismissed(true);
    } catch (error) {
      console.error('Error saving banner dismissal state:', error);
    }
  }, [storageKey]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setIsDismissed(false);
    } catch (error) {
      console.error('Error resetting banner dismissal state:', error);
    }
  }, [storageKey]);

  return { isDismissed, dismiss, reset };
}
